/**
 * EditBundleModal — Edit bundle component items (add, remove, change qty).
 *
 * Opens from the [Edit] button on BundleComponentsSection.
 * Saves via standard Frappe frappe.client.get + frappe.client.save pattern.
 *
 * Plan: 171-bundle-product-ux, Epic 03
 */

import { useState, useEffect, useCallback } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { LinkFieldSearch } from "@/components/filter-builder/LinkFieldSearch";
import { getERPClient } from "@/lib/erp/client";
import { customizationQueryKeys } from "@/features/customization/api/customizationAdminHooks";
import { useAuthStore } from "@/lib/store/authStore";
import { toast } from "sonner";
import { Package, Plus, Trash2, Loader2, AlertTriangle } from "lucide-react";
import type { BundleChildItem } from "@/features/customization/api/customizationAdminTypes";

// ── Types ────────────────────────────────────────────────────

interface EditableRow {
  id: string;
  item_code: string;
  item_name: string;
  qty: number;
  image: string | null;
  row_name: string | null; // Frappe child row name (null for new rows)
}

interface EditBundleModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  bundleName: string;
  itemCode: string;
  initialItems: BundleChildItem[];
}

// ── Helpers ──────────────────────────────────────────────────

function toEditableRows(items: BundleChildItem[]): EditableRow[] {
  return items.map((item) => ({
    id: crypto.randomUUID(),
    item_code: item.item_code,
    item_name: item.item_name,
    qty: item.qty || 1,
    image: item.image,
    row_name: item.row_name || null,
  }));
}

function createEmptyRow(): EditableRow {
  return {
    id: crypto.randomUUID(),
    item_code: "",
    item_name: "",
    qty: 1,
    image: null,
    row_name: null,
  };
}

// ── Save Mutation ────────────────────────────────────────────

function useSaveBundleComponents(
  itemCode: string,
  onSuccess: () => void,
) {
  const queryClient = useQueryClient();
  const tenant = useAuthStore((s) => s.currentTenant);

  return useMutation({
    mutationFn: async ({
      bundleName,
      items,
    }: {
      bundleName: string;
      items: EditableRow[];
    }) => {
      const client = getERPClient();

      // Fetch full doc (gets `modified` for optimistic locking)
      const doc = await client.get<Record<string, unknown>>({
        doctype: "Product Bundle",
        name: bundleName,
      });

      // Replace items child table (filter out empty rows)
      const validItems = items.filter((item) => item.item_code);
      (doc as Record<string, unknown>).items = validItems.map((item, idx) => ({
        doctype: "Product Bundle Item",
        item_code: item.item_code,
        qty: item.qty,
        idx: idx + 1,
        // Preserve row name for existing rows (Frappe reconciliation)
        ...(item.row_name ? { name: item.row_name } : {}),
      }));

      // Save back — plain object, NOT JSON.stringify
      return client.call("frappe.client.save", { doc });
    },
    onSuccess: () => {
      toast.success("Bundle updated");
      queryClient.invalidateQueries({
        queryKey: customizationQueryKeys.bundleConfig(tenant, itemCode),
      });
      onSuccess();
    },
    onError: (error: Error) => {
      toast.error(error.message || "Failed to update bundle");
    },
  });
}

// ── Component ────────────────────────────────────────────────

export function EditBundleModal({
  open,
  onOpenChange,
  bundleName,
  itemCode,
  initialItems,
}: EditBundleModalProps) {
  const [rows, setRows] = useState<EditableRow[]>([]);

  // Re-initialize local state when modal opens
  useEffect(() => {
    if (open) {
      setRows(toEditableRows(initialItems));
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleClose = useCallback(() => {
    onOpenChange(false);
  }, [onOpenChange]);

  const saveMutation = useSaveBundleComponents(itemCode, handleClose);

  // ── Row operations ──────────────────────────────────

  const handleItemSelect = useCallback(
    async (rowId: string, selectedItemCode: string | null) => {
      if (!selectedItemCode) {
        setRows((prev) =>
          prev.map((r) =>
            r.id === rowId
              ? { ...r, item_code: "", item_name: "", image: null }
              : r,
          ),
        );
        return;
      }

      // Fetch item details (name + image)
      try {
        const client = getERPClient();
        const itemData = await client.call<{
          item_name: string;
          image: string | null;
        }>("frappe.client.get_value", {
          doctype: "Item",
          fieldname: ["item_name", "image"],
          filters: { name: selectedItemCode },
        });

        setRows((prev) =>
          prev.map((r) =>
            r.id === rowId
              ? {
                  ...r,
                  item_code: selectedItemCode,
                  item_name: itemData?.item_name || selectedItemCode,
                  image: itemData?.image || null,
                  row_name: null, // New selection = new row for Frappe
                }
              : r,
          ),
        );
      } catch {
        // Fallback: use item_code as name
        setRows((prev) =>
          prev.map((r) =>
            r.id === rowId
              ? { ...r, item_code: selectedItemCode, item_name: selectedItemCode, image: null, row_name: null }
              : r,
          ),
        );
      }
    },
    [],
  );

  const handleQtyChange = useCallback((rowId: string, value: string) => {
    const numValue = parseInt(value, 10);
    if (isNaN(numValue)) return;
    setRows((prev) =>
      prev.map((r) => (r.id === rowId ? { ...r, qty: numValue } : r)),
    );
  }, []);

  const handleQtyBlur = useCallback((rowId: string) => {
    setRows((prev) =>
      prev.map((r) =>
        r.id === rowId ? { ...r, qty: Math.max(1, Math.round(r.qty)) } : r,
      ),
    );
  }, []);

  const handleRemoveRow = useCallback((rowId: string) => {
    setRows((prev) => prev.filter((r) => r.id !== rowId));
  }, []);

  const handleAddRow = useCallback(() => {
    setRows((prev) => [...prev, createEmptyRow()]);
  }, []);

  const handleSave = useCallback(() => {
    saveMutation.mutate({ bundleName, items: rows });
  }, [saveMutation, bundleName, rows]);

  // ── Duplicate detection ──────────────────────────────

  const itemCodeCounts = rows.reduce<Record<string, number>>((acc, r) => {
    if (r.item_code) {
      acc[r.item_code] = (acc[r.item_code] || 0) + 1;
    }
    return acc;
  }, {});

  // ── Render ──────────────────────────────────────────

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[560px] rounded-2xl">
        <DialogHeader>
          <DialogTitle>Edit Bundle Components</DialogTitle>
          <DialogDescription className="sr-only">
            Add, remove, or change quantities of component items in this bundle.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-2 max-h-[60vh] overflow-y-auto py-2">
          {rows.length === 0 ? (
            <div className="text-center py-8 text-sm text-muted-foreground">
              No items in this bundle yet.
              <br />
              <Button
                variant="ghost"
                size="sm"
                className="mt-2"
                onClick={handleAddRow}
              >
                <Plus className="h-3.5 w-3.5 mr-1.5" />
                Add item
              </Button>
            </div>
          ) : (
            rows.map((row) => (
              <BundleEditRow
                key={row.id}
                row={row}
                isDuplicate={(itemCodeCounts[row.item_code] || 0) > 1}
                onItemSelect={(val) => handleItemSelect(row.id, val)}
                onQtyChange={(val) => handleQtyChange(row.id, val)}
                onQtyBlur={() => handleQtyBlur(row.id)}
                onRemove={() => handleRemoveRow(row.id)}
              />
            ))
          )}
        </div>

        {rows.length > 0 && (
          <Button
            variant="ghost"
            size="sm"
            className="text-sm w-fit"
            onClick={handleAddRow}
          >
            <Plus className="h-3.5 w-3.5 mr-1.5" />
            Add item
          </Button>
        )}

        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={saveMutation.isPending}
            className="bg-sky-600 hover:bg-sky-700 text-white"
          >
            {saveMutation.isPending && (
              <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
            )}
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Single Editable Row ─────────────────────────────────────

function BundleEditRow({
  row,
  isDuplicate,
  onItemSelect,
  onQtyChange,
  onQtyBlur,
  onRemove,
}: {
  row: EditableRow;
  isDuplicate: boolean;
  onItemSelect: (value: string | null) => void;
  onQtyChange: (value: string) => void;
  onQtyBlur: () => void;
  onRemove: () => void;
}) {
  return (
    <div className="space-y-1">
      <div className="flex items-center gap-2">
        {/* Thumbnail */}
        {row.image ? (
          <img
            src={row.image}
            alt={row.item_name}
            className="h-8 w-8 rounded-lg object-cover shrink-0"
          />
        ) : (
          <div className="h-8 w-8 rounded-lg bg-muted flex items-center justify-center shrink-0">
            <Package className="h-4 w-4 text-muted-foreground" />
          </div>
        )}

        {/* Item selector */}
        <div className="flex-1 min-w-0">
          <LinkFieldSearch
            doctype="Item"
            value={row.item_code || null}
            onChange={onItemSelect}
            placeholder="Select item..."
            defaultFilters={{ disabled: 0 }}
            className="w-full"
          />
        </div>

        {/* Quantity */}
        <div className="w-16 shrink-0">
          <Input
            type="number"
            min={1}
            value={row.qty}
            onChange={(e) => onQtyChange(e.target.value)}
            onBlur={onQtyBlur}
            className="h-8 text-center text-sm"
            aria-label={`Quantity for ${row.item_name || "item"}`}
          />
        </div>

        {/* Delete */}
        <Button
          variant="ghost"
          size="sm"
          className="h-8 w-8 p-0 shrink-0 text-muted-foreground hover:text-destructive"
          onClick={onRemove}
          aria-label={`Remove ${row.item_name || "item"} from bundle`}
        >
          <Trash2 className="h-3.5 w-3.5" />
        </Button>
      </div>

      {/* Duplicate warning */}
      {isDuplicate && row.item_code && (
        <div className="flex items-center gap-1 ml-10 text-xs text-amber-600 dark:text-amber-400">
          <AlertTriangle className="h-3 w-3 shrink-0" />
          This item is already in the bundle.
        </div>
      )}
    </div>
  );
}
